(function () {
  'use strict';

  const STORAGE_KEY = 'srdDesignerHelpLanguageV1';
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  const COPY = {
    en: {
      title: 'Designer Help',
      searchLabel: 'Search help',
      placeholder: 'Search Designer Help',
      clear: 'Clear',
      close: 'Close Designer Help',
      results: function (count) { return count + (count === 1 ? ' matching topic' : ' matching topics'); },
      noResultsTitle: 'No matching help topics',
      noResultsCopy: 'Try a shorter or more general search.'
    },
    es: {
      title: 'Ayuda del Diseñador',
      searchLabel: 'Buscar ayuda',
      placeholder: 'Buscar en la Ayuda del Diseñador',
      clear: 'Borrar',
      close: 'Cerrar Ayuda del Diseñador',
      results: function (count) { return count + (count === 1 ? ' tema encontrado' : ' temas encontrados'); },
      noResultsTitle: 'No se encontraron temas de ayuda',
      noResultsCopy: 'Pruebe una búsqueda más corta o general.'
    }
  };

  function normalized(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase();
  }

  function preferredLanguage() {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === 'en' || saved === 'es') return saved;
    } catch (error) {
      // Device-local language memory is optional when storage is unavailable.
    }
    const languages = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language || 'en'];
    return languages.some(function (language) { return /^es(?:-|$)/i.test(language); }) ? 'es' : 'en';
  }

  function contextSection(root) {
    const mode = root.getAttribute('data-srd-designer-mode') || '';
    if (mode === 'nitrous') return 'nitrous';
    if (mode === 'standard') return 'standard-safety';
    if (mode === 'blank_canvas') {
      const workspace = root.getAttribute('data-srd-blank-canvas-subfamily')
        || root.getAttribute('data-srd-dcp-workspace-mode')
        || '';
      return workspace === 'die_cutting' ? 'custom-die' : 'custom-print';
    }
    return 'start';
  }

  function initialize(root) {
    if (!root || root.dataset.srdDesignerHelpReady === 'true') return;
    const host = root.querySelector('[data-srd-designer-help]');
    const trigger = root.querySelector('[data-srd-designer-help-trigger]');
    const dialog = host && host.querySelector('[data-srd-help-dialog]');
    if (!host || !trigger || !dialog) return;
    root.dataset.srdDesignerHelpReady = 'true';

    const shell = root.querySelector('.srd-designer__shell');
    const content = dialog.querySelector('[data-srd-help-content]');
    const search = dialog.querySelector('[data-srd-help-search]');
    const status = dialog.querySelector('[data-srd-help-status]');
    const noResults = dialog.querySelector('[data-srd-help-no-results]');
    const title = dialog.querySelector('[data-srd-help-title]');
    const searchLabel = dialog.querySelector('[data-srd-help-search-label]');
    const clearButton = dialog.querySelector('[data-srd-help-clear]');
    const closeButton = dialog.querySelector('[data-srd-help-close]');
    let language = preferredLanguage();
    let returnFocus = null;
    let priorBodyOverflow = '';
    let priorBodyPaddingRight = '';
    let shellAriaHidden = null;
    let shellHadInert = false;
    let currentContext = 'start';

    dialog.querySelectorAll('[data-srd-help-section]').forEach(function (section) {
      section.dataset.searchText = normalized(section.textContent);
    });

    function activePanel() {
      return dialog.querySelector('[data-srd-help-language-panel="' + language + '"]');
    }

    function clearHighlights(scope) {
      (scope || dialog).querySelectorAll('mark[data-srd-help-highlight]').forEach(function (mark) {
        const parent = mark.parentNode;
        parent.replaceChild(document.createTextNode(mark.textContent), mark);
        parent.normalize();
      });
    }

    function normalizedMap(value) {
      let text = '';
      const map = [];
      Array.from(value).forEach(function (character, originalIndex) {
        const plain = normalized(character);
        Array.from(plain).forEach(function (mappedCharacter) {
          text += mappedCharacter;
          map.push(originalIndex);
        });
      });
      return { text: text, map: map };
    }

    function highlightNode(node, query) {
      const mapped = normalizedMap(node.nodeValue || '');
      const ranges = [];
      let from = 0;
      while (query && from < mapped.text.length) {
        const found = mapped.text.indexOf(query, from);
        if (found < 0) break;
        const start = mapped.map[found];
        const endMapped = mapped.map[found + query.length - 1];
        ranges.push([start, endMapped + 1]);
        from = found + query.length;
      }
      ranges.reverse().forEach(function (range) {
        node.splitText(range[1]);
        const match = node.splitText(range[0]);
        const mark = document.createElement('mark');
        mark.dataset.srdHelpHighlight = 'true';
        match.parentNode.replaceChild(mark, match);
        mark.appendChild(match);
      });
    }

    function highlightSection(section, query) {
      const walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          const parent = node.parentElement;
          if (!parent || parent.closest('button, script, style, mark')) return NodeFilter.FILTER_REJECT;
          return normalized(node.nodeValue).indexOf(query) >= 0
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (node) { highlightNode(node, query); });
    }

    function resetSearchVisibility() {
      dialog.querySelectorAll('[data-srd-help-section]').forEach(function (section) {
        section.hidden = false;
      });
      noResults.hidden = true;
      status.textContent = '';
    }

    function runSearch() {
      clearHighlights(dialog);
      const query = normalized(search.value.trim());
      const panel = activePanel();
      resetSearchVisibility();
      if (!query || !panel) return;
      let matches = 0;
      panel.querySelectorAll('[data-srd-help-section]').forEach(function (section) {
        const matched = section.dataset.searchText.indexOf(query) >= 0;
        section.hidden = !matched;
        if (matched) {
          matches += 1;
          section.open = true;
          highlightSection(section, query);
        }
      });
      noResults.hidden = matches !== 0;
      status.textContent = COPY[language].results(matches);
    }

    function openSection(sectionId, options) {
      const panel = activePanel();
      if (!panel) return;
      panel.querySelectorAll('[data-srd-help-section].is-context').forEach(function (section) {
        section.classList.remove('is-context');
      });
      const section = panel.querySelector('[data-srd-help-section="' + sectionId + '"]');
      if (!section) return;
      section.hidden = false;
      section.open = true;
      if (options && options.context) section.classList.add('is-context');
      if (options && options.scroll) {
        section.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    function setLanguage(nextLanguage, remember) {
      if (nextLanguage !== 'en' && nextLanguage !== 'es') return;
      const openIds = Array.from(activePanel()?.querySelectorAll('[data-srd-help-section][open]') || [])
        .map(function (section) { return section.dataset.srdHelpSection; });
      language = nextLanguage;
      dialog.setAttribute('lang', language);
      dialog.querySelectorAll('[data-srd-help-language-panel]').forEach(function (panel) {
        panel.hidden = panel.dataset.srdHelpLanguagePanel !== language;
      });
      dialog.querySelectorAll('[data-srd-help-language]').forEach(function (button) {
        button.setAttribute('aria-pressed', button.dataset.srdHelpLanguage === language ? 'true' : 'false');
      });
      title.textContent = COPY[language].title;
      searchLabel.textContent = COPY[language].searchLabel;
      search.placeholder = COPY[language].placeholder;
      clearButton.textContent = COPY[language].clear;
      closeButton.setAttribute('aria-label', COPY[language].close);
      dialog.querySelector('[data-srd-help-no-results-title]').textContent = COPY[language].noResultsTitle;
      dialog.querySelector('[data-srd-help-no-results-copy]').textContent = COPY[language].noResultsCopy;
      if (remember) {
        try { window.localStorage.setItem(STORAGE_KEY, language); } catch (error) { /* Optional memory. */ }
      }
      clearHighlights(dialog);
      resetSearchVisibility();
      if (search.value.trim()) {
        runSearch();
      } else if (openIds.length) {
        openIds.forEach(function (id) { openSection(id); });
      } else {
        openSection(currentContext, { context: true });
      }
    }

    function lockBackground() {
      priorBodyOverflow = document.body.style.overflow;
      priorBodyPaddingRight = document.body.style.paddingRight;
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      if (scrollbar) document.body.style.paddingRight = scrollbar + 'px';
      document.body.style.overflow = 'hidden';
      if (shell) {
        shellAriaHidden = shell.getAttribute('aria-hidden');
        shellHadInert = shell.hasAttribute('inert');
        shell.setAttribute('inert', '');
        shell.setAttribute('aria-hidden', 'true');
      }
    }

    function unlockBackground() {
      document.body.style.overflow = priorBodyOverflow;
      document.body.style.paddingRight = priorBodyPaddingRight;
      if (shell) {
        if (!shellHadInert) shell.removeAttribute('inert');
        if (shellAriaHidden === null) shell.removeAttribute('aria-hidden');
        else shell.setAttribute('aria-hidden', shellAriaHidden);
      }
    }

    function openHelp() {
      if (dialog.open) return;
      returnFocus = document.activeElement;
      currentContext = contextSection(root);
      search.value = '';
      clearHighlights(dialog);
      resetSearchVisibility();
      setLanguage(language, false);
      openSection(currentContext, { context: true });
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      lockBackground();
      window.requestAnimationFrame(function () { search.focus(); });
    }

    function closeHelp() {
      if (!dialog.open && !dialog.hasAttribute('open')) return;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      unlockBackground();
      if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
      returnFocus = null;
    }

    function trapFocus(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeHelp();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(function (element) {
        return !element.hidden && element.offsetParent !== null;
      });
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    trigger.addEventListener('click', openHelp);
    closeButton.addEventListener('click', closeHelp);
    clearButton.addEventListener('click', function () {
      search.value = '';
      clearHighlights(dialog);
      resetSearchVisibility();
      openSection(currentContext, { context: true });
      search.focus();
    });
    search.addEventListener('input', runSearch);
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeHelp();
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeHelp();
    });
    dialog.addEventListener('keydown', trapFocus);
    dialog.addEventListener('click', function (event) {
      const languageButton = event.target.closest('[data-srd-help-language]');
      if (languageButton) {
        setLanguage(languageButton.dataset.srdHelpLanguage, true);
        return;
      }
      const jump = event.target.closest('[data-srd-help-jump]');
      if (jump) openSection(jump.dataset.srdHelpJump, { scroll: true });
    });
    dialog.addEventListener('close', unlockBackground);
    setLanguage(language, false);
  }

  function initializeAll(scope) {
    (scope || document).querySelectorAll('[data-srd-designer]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initializeAll(document); }, { once: true });
  } else {
    initializeAll(document);
  }
  document.addEventListener('shopify:section:load', function (event) { initializeAll(event.target); });
}());
